import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export interface TrailPreference {
  id: string;
  user_id: string;
  trail_id: string;
  is_favorite: boolean;
  want_to_go: boolean;
  created_at: string;
  updated_at: string;
}

export interface TrailPreferenceWithTrail extends TrailPreference {
  trails: {
    id: string;
    name: string;
    length_miles: number | null;
  } | null;
}

export const useTrailPreference = (trailId: string | undefined) => {
  const { user } = useAuth();

  const { data: preference, isLoading } = useQuery({
    queryKey: ["trail-preference", trailId, user?.id],
    queryFn: async () => {
      if (!user || !trailId) return null;

      const { data, error } = await supabase
        .from("user_trail_preferences")
        .select("*")
        .eq("user_id", user.id)
        .eq("trail_id", trailId)
        .maybeSingle();

      if (error) throw error;
      return data as TrailPreference | null;
    },
    enabled: !!user && !!trailId,
  });

  return {
    preference,
    isLoading,
    isFavorite: preference?.is_favorite ?? false,
    wantToGo: preference?.want_to_go ?? false,
  };
};

export const useUserTrailPreferences = () => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["user-trail-preferences", user?.id],
    queryFn: async () => {
      if (!user) return { favorites: [], wantToGo: [] };

      const { data, error } = await supabase
        .from("user_trail_preferences")
        .select(`
          *,
          trails (
            id,
            name,
            length_miles
          )
        `)
        .eq("user_id", user.id);

      if (error) throw error;

      const preferences = data as TrailPreferenceWithTrail[];
      
      return {
        favorites: preferences.filter(p => p.is_favorite),
        wantToGo: preferences.filter(p => p.want_to_go),
        all: preferences,
      };
    },
    enabled: !!user,
  });

  return {
    favorites: data?.favorites ?? [],
    wantToGo: data?.wantToGo ?? [],
    all: data?.all ?? [],
    isLoading,
    favoritesCount: data?.favorites?.length ?? 0,
    wantToGoCount: data?.wantToGo?.length ?? 0,
  };
};

export const useToggleFavorite = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ trailId, currentValue }: { trailId: string; currentValue: boolean }) => {
      if (!user) throw new Error("Must be logged in");

      // Check if preference exists
      const { data: existing } = await supabase
        .from("user_trail_preferences")
        .select("id, is_favorite, want_to_go")
        .eq("user_id", user.id)
        .eq("trail_id", trailId)
        .maybeSingle();

      const newValue = !currentValue;

      if (existing) {
        // Update existing preference
        const { error } = await supabase
          .from("user_trail_preferences")
          .update({ is_favorite: newValue, updated_at: new Date().toISOString() })
          .eq("id", existing.id);

        if (error) throw error;
      } else {
        // Create new preference
        const { error } = await supabase
          .from("user_trail_preferences")
          .insert({
            user_id: user.id,
            trail_id: trailId,
            is_favorite: newValue,
            want_to_go: false,
          });

        if (error) throw error;
      }

      return newValue;
    },
    onSuccess: (newValue, { trailId }) => {
      queryClient.invalidateQueries({ queryKey: ["trail-preference", trailId] });
      queryClient.invalidateQueries({ queryKey: ["user-trail-preferences"] });
      toast.success(newValue ? "Added to favorites" : "Removed from favorites");
    },
    onError: (error) => {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorite");
    },
  });
};

export const useToggleWantToGo = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ trailId, currentValue }: { trailId: string; currentValue: boolean }) => {
      if (!user) throw new Error("Must be logged in");

      // Check if preference exists
      const { data: existing } = await supabase
        .from("user_trail_preferences")
        .select("id, is_favorite, want_to_go")
        .eq("user_id", user.id)
        .eq("trail_id", trailId)
        .maybeSingle();

      const newValue = !currentValue;

      if (existing) {
        // Update existing preference
        const { error } = await supabase
          .from("user_trail_preferences")
          .update({ want_to_go: newValue, updated_at: new Date().toISOString() })
          .eq("id", existing.id);

        if (error) throw error;
      } else {
        // Create new preference
        const { error } = await supabase
          .from("user_trail_preferences")
          .insert({
            user_id: user.id,
            trail_id: trailId,
            is_favorite: false,
            want_to_go: newValue,
          });

        if (error) throw error;
      }

      return newValue;
    },
    onSuccess: (newValue, { trailId }) => {
      queryClient.invalidateQueries({ queryKey: ["trail-preference", trailId] });
      queryClient.invalidateQueries({ queryKey: ["user-trail-preferences"] });
      toast.success(newValue ? "Added to wishlist" : "Removed from wishlist");
    },
    onError: (error) => {
      console.error("Error toggling want to go:", error);
      toast.error("Failed to update wishlist");
    },
  });
};
